import type { Event } from '@wineryxplorer/shared';

interface EventTimeDisplayProps {
  event: Event;
  timezone?: string;
}

/**
 * Formats a date to the user's local timezone
 */
const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Formats time in 12-hour format
 */
const formatTime = (startTime: { hours: number; minutes: number }): string => {
  const hours = startTime.hours;
  const minutes = startTime.minutes;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, '0');
  
  return `${displayHours}:${displayMinutes} ${period}`;
};

/**
 * Calculates end time from start time and duration
 */
const calculateEndTime = (
  startDate: Date,
  startTime: { hours: number; minutes: number },
  duration: number
): Date => {
  const date = new Date(startDate);
  date.setHours(startTime.hours, startTime.minutes, 0, 0);
  date.setMinutes(date.getMinutes() + duration);
  return date;
};

/**
 * Formats end time from duration
 */
const formatEndTime = (
  startDate: Date,
  startTime: { hours: number; minutes: number },
  duration: number
): string => {
  const endDate = calculateEndTime(startDate, startTime, duration);
  const hours = endDate.getHours();
  const minutes = endDate.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, '0');
  
  return `${displayHours}:${displayMinutes} ${period}`;
};

/**
 * EventTimeDisplay component
 * Displays event date and time information with timezone conversion
 */
export const EventTimeDisplay = ({ event }: EventTimeDisplayProps) => {
  // Show "All Day" for events without specific time
  if (event.isAllDay || !event.startTime) {
    return (
      <div className="space-y-2">
        <div className="flex items-center text-gray-700">
          <svg
            className="w-5 h-5 mr-3 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <div>
            <div className="font-medium">{formatDate(event.startDate)}</div>
            <div className="text-sm text-gray-600 mt-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                All Day Event
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show specific time for timed events
  return (
    <div className="space-y-2">
      <div className="flex items-start text-gray-700">
        <svg
          className="w-5 h-5 mr-3 mt-0.5 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <div>
          <div className="font-medium">{formatDate(event.startDate)}</div>
          <div className="text-sm text-gray-600 mt-1">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                {formatTime(event.startTime)}
                {event.duration && (
                  <>
                    {' - '}
                    {formatEndTime(event.startDate, event.startTime, event.duration)}
                    <span className="text-gray-500 ml-1">
                      ({event.duration} min)
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 ml-8">
        Times shown in your local timezone
      </div>
    </div>
  );
};
